<template>
    <div class="bookmarksearch-page">
        <header class="page-header">
            <div class="lang-switch">
                <a :class="{ active: $lang === 'ja' }" @click="switchLang('ja')">
                    <img :src="flagJa" class="lang-flag" alt="日本語"> 日本語
                </a>
                <span class="lang-sep">|</span>
                <a :class="{ active: $lang === 'en' }" @click="switchLang('en')">
                    <img :src="flagEn" class="lang-flag" alt="English"> English
                </a>
            </div>
            <h1>{{ $t('bookmarksearch.title') }}</h1>
            <router-link :to="{ path: '/Index', query: { lang: $lang } }" class="back-link">{{ $t('common.backLink') }}</router-link>
        </header>

        <main class="page-main">
            <section class="hero">
                <p class="hero-badge">{{ $t('bookmarksearch.heroBadge') }}</p>
                <h2 class="hero-title">{{ $t('bookmarksearch.tagline') }}</h2>
                <p class="hero-sub">{{ $t('bookmarksearch.subline') }}</p>
                <div class="hero-actions">
                    <a :href="purchaseUrl" target="_blank" rel="noopener" class="purchase-link" @click="track('click', 'purchase')">
                        <i class="el-icon-shopping-cart-full"></i>
                        {{ $t('common.purchaseLabel') }}
                    </a>
                    <a :href="downloadUrl" class="download-link hero-download" @click="track('click', 'download_trial')">
                        <i class="el-icon-download"></i>
                        {{ $t('common.trialDownload') }}
                    </a>
                </div>
                <p class="hero-note">{{ $t('bookmarksearch.heroNote', { trialDays: trialDays, price: price }) }}</p>
            </section>

            <h2 class="section-title">{{ $t('bookmarksearch.featuresTitle') }}</h2>
            <div class="feature-cards">
                <div class="feature-card" v-for="(card, idx) in $t('bookmarksearch.featureCards')" :key="idx">
                    <i :class="card.icon"></i>
                    <h3>{{ card.title }}</h3>
                    <p>{{ card.desc }}</p>
                </div>
            </div>
            <ul class="feature-list">
                <li v-for="(item, index) in featureList" :key="index">{{ item }}</li>
            </ul>
            <p class="note">{{ $t('bookmarksearch.disclaimer') }}</p>

            <h2 class="section-title">{{ $t('bookmarksearch.screenshotTitle') }}</h2>
            <div class="screenshot-box">
                <img v-if="screenshotShown" :src="screenshotUrl" :alt="$t('bookmarksearch.screenshotAlt')" @error="screenshotShown = false" />
                <p v-else class="screenshot-placeholder">{{ $t('bookmarksearch.screenshotPlaceholder') }}</p>
            </div>

            <h2 class="section-title">{{ $t('bookmarksearch.priceTitle') }}</h2>
            <p class="price">{{ $t('bookmarksearch.priceLine', { price: price, trialDays: trialDays }) }}</p>
            <p class="purchase-row">
                <a :href="purchaseUrl" target="_blank" rel="noopener" class="purchase-link" @click="track('click', 'purchase')">
                    <i class="el-icon-shopping-cart-full"></i>
                    {{ $t('common.purchaseLabel') }}
                </a>
            </p>

            <h2 class="section-title">{{ $t('bookmarksearch.downloadTitle') }}</h2>
            <p class="download-desc" v-html="$t('bookmarksearch.downloadDesc', { version: version })"></p>
            <p>
                <a :href="downloadUrl" class="download-link" @click="track('click', 'download_trial')">
                    {{ $t('bookmarksearch.downloadLink', { version: version, zipName: zipName }) }}
                </a>
            </p>

            <h2 class="section-title">{{ $t('bookmarksearch.faqTitle') }}</h2>
            <div class="faq-item" v-for="(item, idx) in $t('bookmarksearch.faqItems')" :key="idx">
                <details>
                    <summary>{{ item.q }}</summary>
                    <p>{{ item.a }}</p>
                </details>
            </div>

            <h2 class="section-title">{{ $t('bookmarksearch.sellerTitle') }}</h2>
            <ul class="seller-info">
                <li v-for="(item, idx) in $t('bookmarksearch.sellerInfo')" :key="idx">{{ item }}</li>
            </ul>

            <h2 class="section-title">{{ $t('bookmarksearch.manualTitle') }}</h2>
            <p class="manual-row">
                <a :href="manualJaUrl" target="_blank" rel="noopener" class="download-link" @click="track('click', 'manual_ja')">
                    <i class="el-icon-document"></i> {{ $t('bookmarksearch.manualJa') }}
                </a>
                <a :href="manualUrl" target="_blank" rel="noopener" class="download-link" @click="track('click', 'manual_en')">
                    <i class="el-icon-document"></i> {{ $t('bookmarksearch.manualEn') }}
                </a>
            </p>
            <p class="note" v-html="$t('common.contact')"></p>
        </main>

        <footer class="page-footer">
            <p>{{ $t('common.copyright') }}</p>
        </footer>
    </div>
</template>

<script>
import flagJa from '../../assets/images/flag-jp.svg'
import flagEn from '../../assets/images/flag-gb.svg'

import {
  BOOKMARKSEARCH_VERSION,
  BOOKMARKSEARCH_ZIP_NAME,
  BOOKMARKSEARCH_DOWNLOAD_URL,
  BOOKMARKSEARCH_PURCHASE_URL,
  BOOKMARKSEARCH_TRIAL_DAYS,
  BOOKMARKSEARCH_SCREENSHOT_URL,
  BOOKMARKSEARCH_MANUAL_URL,
  BOOKMARKSEARCH_MANUAL_JA_URL
} from './bookmarksearchConst'

export default {
  data () {
    return {
      version: BOOKMARKSEARCH_VERSION,
      zipName: BOOKMARKSEARCH_ZIP_NAME,
      downloadUrl: BOOKMARKSEARCH_DOWNLOAD_URL,
      purchaseUrl: BOOKMARKSEARCH_PURCHASE_URL,
      trialDays: BOOKMARKSEARCH_TRIAL_DAYS,
      screenshotUrl: BOOKMARKSEARCH_SCREENSHOT_URL,
      manualUrl: BOOKMARKSEARCH_MANUAL_URL,
      manualJaUrl: BOOKMARKSEARCH_MANUAL_JA_URL,
      flagJa: flagJa,
      flagEn: flagEn,
      screenshotShown: true
    }
  },
  computed: {
    price () {
      return this.$t('bookmarksearch.price')
    },
    featureList () {
      const params = { trialDays: this.trialDays }
      return [this.$t('bookmarksearch.feature'), ...this.$t('bookmarksearch.featureList', params)]
    }
  },
  created () {
    this.applyLanguageFromQuery()
  },
  mounted () {
    this.applyLanguageFromQuery()
    this.injectJsonLd()
  },
  watch: {
    '$route.query.lang' () {
      this.applyLanguageFromQuery()
    }
  },
  methods: {
    track (action, label) {
      if (window.gtag) {
        window.gtag('event', action, { event_category: 'bookmarksearch', event_label: label })
      }
      if (window.fbq) {
        window.fbq('trackCustom', 'BookMarkSearch_' + action, { label: label })
      }
    },
    getLanguageParam () {
      const routeLang = this.$route && this.$route.query ? this.$route.query.lang : ''
      if (Array.isArray(routeLang)) {
        return routeLang[0] || ''
      }
      if (routeLang) {
        return routeLang
      }
      if (typeof window === 'undefined') {
        return ''
      }
      const match = window.location.search.match(/[?&]lang=(ja|en)(?:&|$)/i)
      return match ? match[1] : ''
    },
    applyLanguageFromQuery () {
      const lang = String(this.getLanguageParam() || '').toLowerCase()
      if (lang === 'ja' || lang === 'en') {
        this.$switchLang(lang)
      }
    },
    switchLang (lang) {
      this.$switchLang(lang)
      const currentLang = Array.isArray(this.$route.query.lang) ? this.$route.query.lang[0] : this.$route.query.lang
      if (currentLang === lang) {
        return
      }
      const query = Object.assign({}, this.$route.query, { lang: lang })
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    injectJsonLd () {
      const existing = document.getElementById('bookmarksearch-jsonld')
      if (existing) existing.remove()
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.id = 'bookmarksearch-jsonld'
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: this.$t('bookmarksearch.title'),
        alternateName: 'BookMarkSearch',
        url: 'https://www.searchplusplus.jp/#/bookmarksearch',
        description: this.$t('bookmarksearch.subline'),
        operatingSystem: 'Windows 10, Windows 11 (64-bit)',
        applicationCategory: 'UtilitiesApplication',
        offers: {
          '@type': 'Offer',
          price: this.$t('bookmarksearch.price').replace(/[^\d]/g, ''),
          priceCurrency: 'JPY'
        }
      })
      document.head.appendChild(script)
    }
  }
}
</script>

<style scoped>
.bookmarksearch-page {
    max-width: 900px;
    margin: 0 auto;
    padding: 24px 20px 40px;
    min-height: 100vh;
    background: #fff;
}

.page-header {
    text-align: center;
    padding-bottom: 24px;
    border-bottom: 2px solid #13a06a;
    margin-bottom: 32px;
}

.page-header h1 {
    margin: 0 0 16px;
    color: #13a06a;
    font-size: 28px;
}

.lang-switch {
    float: right;
    margin-top: 6px;
    font-size: 13px;
}

.lang-switch a {
    cursor: pointer;
    color: #909399;
    text-decoration: none;
    font-weight: 500;
}

.lang-switch a:hover,
.lang-switch a.active {
    color: #13a06a;
}

.lang-flag {
    width: 19px;
    height: 13px;
    vertical-align: -2px;
    margin-right: 4px;
    border-radius: 2px;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
}

.lang-sep {
    margin: 0 6px;
    color: #dcdfe6;
}

.back-link {
    color: #409EFF;
    text-decoration: none;
    font-size: 14px;
}

.back-link:hover {
    text-decoration: underline;
}

.page-main {
    line-height: 1.8;
    color: #303133;
}

.price {
    font-size: 18px;
    font-weight: 700;
    color: #13a06a;
    margin: 0 0 16px;
}

.purchase-row {
    margin: 0 0 8px;
}

.purchase-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: #13a06a;
    color: #fff;
    text-decoration: none;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 700;
    box-shadow: 0 2px 8px rgba(19, 160, 106, 0.35);
    transition: background 0.2s ease, transform 0.2s ease;
}

.purchase-link i {
    font-size: 20px;
}

.purchase-link:hover {
    background: #0f8658;
    transform: translateY(-1px);
    color: #fff;
}

.section-title {
    font-size: 20px;
    color: #2c3e50;
    margin: 28px 0 12px;
}

.feature-list {
    padding-left: 24px;
    margin: 0;
}

.download-desc {
    margin: 0 0 12px;
}

.download-link {
    display: inline-block;
    margin: 8px 0;
    padding: 10px 16px;
    background: #13a06a;
    color: #fff;
    text-decoration: none;
    border-radius: 6px;
    font-weight: 600;
}

.download-link:hover {
    background: #0f8658;
}

.manual-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 8px 0;
}

.manual-row .download-link i {
    margin-right: 6px;
}

.note {
    font-size: 14px;
    color: #606266;
    margin-top: 20px;
}

.page-footer {
    margin-top: 48px;
    padding-top: 20px;
    border-top: 1px solid #ebeef5;
    text-align: center;
    color: #909399;
    font-size: 14px;
}

.hero {
    padding: 8px 0 24px;
    margin-bottom: 8px;
    text-align: center;
    border-bottom: 1px solid #ebeef5;
}

.hero-badge {
    display: inline-block;
    margin: 0 0 12px;
    padding: 4px 14px;
    background: #e6f7f0;
    color: #0f8658;
    border: 1px solid #b7e6d3;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 700;
}

.hero-title {
    margin: 0 0 12px;
    font-size: 30px;
    color: #13a06a;
}

.hero-sub {
    margin: 0 0 20px;
    font-size: 16px;
    color: #303133;
}

.hero-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
}

.hero-download {
    background: #fff;
    color: #13a06a;
    border: 2px solid #13a06a;
}

.hero-download:hover {
    background: #e6f7f0;
    color: #0f8658;
}

.hero-note {
    margin: 14px 0 0;
    font-size: 13px;
    color: #909399;
}

.feature-cards {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    margin: 0 0 16px;
}

.feature-card {
    flex: 1 1 220px;
    padding: 18px;
    background: #fafbfc;
    border: 1px solid #ebeef5;
    border-radius: 8px;
}

.feature-card i {
    display: block;
    margin-bottom: 8px;
    font-size: 26px;
    color: #13a06a;
}

.feature-card h3 {
    margin: 0 0 8px;
    font-size: 16px;
    color: #2c3e50;
}

.feature-card p {
    margin: 0;
    font-size: 13px;
    line-height: 1.7;
    color: #606266;
}

.screenshot-box {
    padding: 20px;
    background: #fafbfc;
    border: 2px dashed #d3dce6;
    border-radius: 8px;
    text-align: center;
    color: #909399;
}

.screenshot-box img {
    max-width: 100%;
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
}

.screenshot-placeholder {
    margin: 0;
    font-size: 14px;
}

.faq-item {
    margin: 0 0 8px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
}

.faq-item summary {
    padding: 12px 16px;
    cursor: pointer;
    font-weight: 600;
    color: #303133;
}

.faq-item details[open] summary {
    border-bottom: 1px solid #ebeef5;
}

.faq-item p {
    margin: 0;
    padding: 12px 16px;
    font-size: 14px;
    color: #606266;
}

.seller-info {
    margin: 0;
    padding-left: 20px;
    font-size: 14px;
    line-height: 2;
    color: #606266;
}
</style>
